/**
 * The data-access boundary. The game/core never import Firebase directly —
 * everything flows through this typed facade, so the backend is swappable
 * (real Firebase ↔ local fallback) and the rest of the app stays clean.
 *
 * TIERED MODEL (docs/BARRAGE_IMPLEMENTATION_PLAN.md §2 "Tiered auth"):
 *  - **anon**  = basic online play only: play + global leaderboard + "players
 *    online" presence. The tease.
 *  - **named** = a full account (email/password, links the anon credential so the
 *    uid + high score carry over). Unlocks XP, stickers, tokens, wheels, profile,
 *    events — i.e. everything that "collects".
 *
 * Network methods that require a named account throw {@link RequiresAccountError}
 * for anonymous users; the UI catches it and shows the "log in to collect" tease.
 * Each method below is tagged ANON-ALLOWED or NAMED-ONLY.
 */
import type { RunRecord } from '../core/types';
import type {
  EventClaimResult,
  EventDoc,
  GameId,
  NetworkStats,
  NewsItem,
  RunReward,
  RunSummary,
  StickerCatalogItem,
  UserCard,
  UserProfile,
  WheelResult,
} from './dataModel';

export type { RunReward } from './dataModel';

/** A leaderboard row as the UI consumes it. (Distinct from the Firestore {@link import('./dataModel').LeaderboardEntry}.) */
export interface ScoreEntry {
  uid: string;
  name: string;
  score: number;
  /** Epoch ms. */
  createdAt: number;
  /** The `runs/{id}` of the best run, when recorded — lets LIVE replay it. */
  runId?: string | null;
}

export type ServicesMode = 'firebase' | 'local';

/** The two auth tiers. `anon` = play + leaderboard only; `named` = full network. */
export type AccountTier = 'anon' | 'named';

/**
 * Thrown by NAMED-ONLY methods when the current user is still anonymous. The UI
 * catches this to surface the "log in to collect" tease instead of an error.
 */
export class RequiresAccountError extends Error {
  constructor(message = 'This feature requires a named account. Please log in or sign up.') {
    super(message);
    this.name = 'RequiresAccountError';
  }
}

/** The signed-in user as the UI sees them. */
export interface CurrentUser {
  uid: string;
  /** Display name; null for an anonymous user. */
  name: string | null;
  /** Account email; null for anonymous users (and where the provider hides it). */
  email: string | null;
  /**
   * True once the account email is PROVEN via the verification link (Google
   * sign-ins arrive verified; fresh email/password sign-ups don't). Gates the
   * verified-email admin allowlist and the Settings "verify your email" prompt.
   * Always false for anon; the offline LocalBackend reports true.
   */
  emailVerified: boolean;
  tier: AccountTier;
}

/** The result of submitting a run for server validation. */
export interface SubmitRunResult {
  /** Authoritative score the server computed by re-simulating the record. */
  score: number;
  /** The user's personal best after this submission. */
  best: number;
  /** False if the server rejected the run (e.g. score diverged beyond tolerance). */
  accepted: boolean;
  /**
   * The stored run's id (so the just-finished run can be watched/starred straight
   * from the game-over screen). null when the run wasn't persisted (rejected/offline).
   */
  runId?: string | null;
  /**
   * The XP/level/token/achievement deltas a NAMED account earned (for the
   * after-game XP bar + level-up ceremony). Null/absent for anonymous users.
   */
  reward?: RunReward | null;
  /**
   * 1-based rank on TODAY's daily leaderboard (accepted DAILY submissions only —
   * all tiers rank). Null/absent for normal runs.
   */
  dailyRank?: number | null;
}

export interface GameServices {
  /** Which backend is active — surfaced in the UI so it's honest about offline. */
  readonly mode: ServicesMode;

  /** The current auth tier. `anon` until {@link GameServices.signUp}/{@link GameServices.logIn}. */
  readonly tier: AccountTier;

  // ---------------------------------------------------------------------------
  // Lifecycle (ANON-ALLOWED)
  // ---------------------------------------------------------------------------

  /** Boot SDKs, enable offline persistence, anonymous sign-in. Returns the uid. */
  init(): Promise<{ uid: string }>;

  /** Tear down listeners / connections. */
  dispose(): void;

  // ---------------------------------------------------------------------------
  // Account surface
  // ---------------------------------------------------------------------------

  /** The signed-in user (uid + name + tier). ANON-ALLOWED. */
  currentUser(): CurrentUser;

  /**
   * Create a named account and LINK the current anonymous credential, so the uid
   * and high score carry over. Transitions {@link GameServices.tier} to `named`.
   * ANON-ALLOWED (it is the upgrade path).
   */
  signUp(email: string, password: string, name: string): Promise<CurrentUser>;

  /** Log in to an existing named account. ANON-ALLOWED (the upgrade path). */
  logIn(email: string, password: string): Promise<CurrentUser>;

  /**
   * Sign in with Google, LINKING it to the current anonymous credential so the uid
   * (and its leaderboard high score) carries over — the same anon→named upgrade as
   * {@link GameServices.signUp}, via the Google provider instead of email/password.
   * Transitions {@link GameServices.tier} to `named`. ANON-ALLOWED (the upgrade
   * path). Throws if the Google account is already linked to a different uid.
   */
  signInWithGoogle(): Promise<CurrentUser>;

  /** Sign out the named account (returns to anonymous play). NAMED-ONLY. */
  logOut(): Promise<void>;

  /** Send a password-reset email. ANON-ALLOWED. */
  resetPassword(email: string): Promise<void>;

  /**
   * Re-send the account-email verification link (sign-up already sends one
   * automatically). Verifying proves inbox ownership — it's what activates the
   * verified-email admin allowlist. NAMED-ONLY (anonymous users have no email).
   * A no-op offline.
   */
  resendVerificationEmail(): Promise<void>;

  /**
   * Persist the user's preferred UI language to their account (`users/{uid}.language`,
   * a self-writable field). Best-effort and a NO-OP for anonymous users (who have no
   * profile doc) — the device-local choice (localStorage) is what actually drives the
   * UI; this just travels the preference across devices. ANON-ALLOWED.
   */
  setLanguage(code: string): Promise<void>;

  /**
   * Change the account display name: updates the auth profile (the token's `name`
   * claim is what the Function writes to the leaderboard/profile) and best-effort
   * mirrors it to the self-writable `users/{uid}.name`. Public copies (cards,
   * existing leaderboard rows) refresh on the next server write. NAMED-ONLY.
   */
  setDisplayName(name: string): Promise<void>;

  // ---------------------------------------------------------------------------
  // Play + leaderboard (ANON-ALLOWED)
  // ---------------------------------------------------------------------------

  /**
   * Submit a full {@link RunRecord} (seed + input timeline) for SERVER validation.
   * The server re-runs the deterministic sim to compute the authoritative score
   * (anti-cheat) and updates the leaderboard. ANON-ALLOWED (leaderboard only;
   * named accounts additionally earn XP/tokens server-side).
   *
   * @param gameId which game the run belongs to (POPSTAR is multi-game). Stamps
   *   the run + the game's `leaderboards/{gameId}/scores/{uid}` entry. Optional —
   *   defaults to {@link import('./dataModel').DEFAULT_GAME_ID} (`'barrage'`), so
   *   existing single-game callers keep working unchanged.
   * @param opts `daily: true` marks a DAILY RUN — the server independently derives
   *   the day's shared seed and REJECTS a mismatched record, then ranks the run on
   *   the day's board (and, named, advances the streak + pays the streak bonus).
   */
  submitRun(
    record: RunRecord,
    gameId?: GameId,
    opts?: { daily?: boolean },
  ): Promise<SubmitRunResult>;

  /**
   * TODAY's (UTC) Daily Run leaderboard — top-N of the day's best scores.
   * ANON-ALLOWED (public read, same shape as the all-time board).
   */
  getDailyLeaderboard(max?: number, gameId?: GameId): Promise<ScoreEntry[]>;

  /**
   * The newest validated runs, for the LIVE programme guide (compact summaries;
   * short sub-5s runs are filtered out). ANON-ALLOWED (runs are public).
   */
  getRecentRuns(max?: number, gameId?: GameId): Promise<RunSummary[]>;

  /** Admin-curated LIVE picks (`runs` docs flagged `featured`). ANON-ALLOWED. */
  getFeaturedRuns(max?: number, gameId?: GameId): Promise<RunSummary[]>;

  /**
   * @deprecated Transitional shim while the React shell migrates from raw scores
   * to {@link GameServices.submitRun}. Persists a bare score and returns the
   * resulting personal best. Remove once App.tsx submits a {@link RunRecord}.
   */
  submitScore(score: number): Promise<number>;

  /**
   * Top-N scores for a game's leaderboard (default 10), highest first.
   * ANON-ALLOWED.
   *
   * @param gameId which game's `leaderboards/{gameId}/scores` to read. Optional —
   *   defaults to {@link import('./dataModel').DEFAULT_GAME_ID} (`'barrage'`).
   */
  getLeaderboard(max?: number, gameId?: GameId): Promise<ScoreEntry[]>;

  /**
   * Current user's personal best for a game. ANON-ALLOWED.
   *
   * @param gameId which game's leaderboard entry to read. Optional — defaults to
   *   {@link import('./dataModel').DEFAULT_GAME_ID} (`'barrage'`).
   */
  getHighScore(gameId?: GameId): Promise<number>;

  /**
   * Subscribe to the live "players online" count. Calls back immediately with the
   * current value, then on every change. Returns an unsubscribe. ANON-ALLOWED.
   */
  subscribeOnlineCount(cb: (count: number) => void): () => void;

  /**
   * The NETWORK-WIDE stats aggregate for a game (recent average score, total runs,
   * network best, and per-achievement unlock counts) — the Stats → Network tab.
   * Reads the public `stats/{gameId}` doc; returns a zeroed snapshot when none
   * exists yet. ANON-ALLOWED (public read).
   *
   * @param gameId which game's aggregate to read. Optional — defaults to
   *   {@link import('./dataModel').DEFAULT_GAME_ID} (`'barrage'`).
   */
  getNetworkStats(gameId?: GameId): Promise<NetworkStats>;

  // ---------------------------------------------------------------------------
  // Network / collection surface (NAMED-ONLY — anon throws RequiresAccountError)
  // ---------------------------------------------------------------------------

  /** The named user's full profile (xp/level/tokens/equipped). NAMED-ONLY. */
  getProfile(): Promise<UserProfile>;

  /** The public sticker catalog. NAMED-ONLY (collection is a named feature). */
  getStickerCatalog(): Promise<StickerCatalogItem[]>;

  /** The ids of stickers the current user owns. NAMED-ONLY. */
  getOwnedStickers(): Promise<string[]>;

  /** Equip an owned sticker into a slot (ownership checked server-side). NAMED-ONLY. */
  equipSticker(slot: 1 | 2, stickerId: string): Promise<void>;

  /** Spin a prize wheel; the SERVER picks the outcome and applies the grant. NAMED-ONLY. */
  spinWheel(wheelId: string): Promise<WheelResult>;

  /** Buy a sticker with tokens (price + balance enforced server-side). NAMED-ONLY. */
  buySticker(stickerId: string): Promise<void>;

  /** The in-app news feed. NAMED-ONLY. */
  getNews(): Promise<NewsItem[]>;

  /**
   * The currently-claimable seasonal event (the active `catalogEvents/*` doc whose
   * window contains now), or null when there's no live event. Drives the Stickers →
   * Seasonal tab (active only when an event exists). ANON-ALLOWED (public read).
   */
  getActiveEvent(): Promise<EventDoc | null>;

  /**
   * Claim a seasonal event's one-off prize. The SERVER applies the grant and
   * DEDUPLICATES the claim (a second claim reports `alreadyClaimed`). NAMED-ONLY.
   */
  claimEvent(eventId: string): Promise<EventClaimResult>;

  // ---------------------------------------------------------------------------
  // Replay + showcase
  //
  // Every validated run is ALWAYS stored at `runs/{runId}` by submitRun (the
  // leaderboard + anti-cheat record). On top of that flat record sit TWO curated
  // tiers the user explicitly opts into:
  //
  //  - SAVED: the user's private library. Free 3 / plus 10 / pro 50 (see
  //    {@link import('./dataModel').savedReplayQuota}; stored as
  //    {@link import('./dataModel').UserProfile.savedReplayIds}). The user SAVES a
  //    run after game-over ("Save replay") or from My Replays. `getMyReplays` now
  //    returns the SAVED collection — NOT every run the user has ever played.
  //  - STARRED: the public showcase pin on the user's `cards/{uid}` card. Free 1 /
  //    plus 5 / pro 25 ({@link import('./dataModel').replayStarQuota}). A run must
  //    be SAVED before it can be STARRED (the starred set is a subset of saved).
  //
  // A replay re-runs through the deterministic core (the same "one sim, three
  // jobs" mechanism as anti-cheat).
  //
  // Gating: WRITES (save/unsave, star/unstar) and "my replays" are NAMED-ONLY (a
  // named account's own runs). READS of a public card / a public run are
  // ANON-ALLOWED.
  // ---------------------------------------------------------------------------

  /**
   * The calling user's SAVED replay collection (the run docs whose ids are in
   * {@link import('./dataModel').UserProfile.savedReplayIds}), newest first, as
   * compact {@link RunSummary}s (no input timeline). This is the curated library —
   * NOT every run ever played (those still live at `runs/{runId}` for the
   * leaderboard). Resolved by fetching the saved run docs by id (no composite
   * index). NAMED-ONLY.
   *
   * @param max optional cap on rows (the backend clamps to a sane ceiling).
   * @param gameId which game's saved runs to enumerate (the flat `runs` collection
   *   is filtered by the run's `gameId`). Optional — defaults to
   *   {@link import('./dataModel').DEFAULT_GAME_ID} (`'barrage'`).
   */
  getMyReplays(max?: number, gameId?: GameId): Promise<RunSummary[]>;

  /**
   * SAVE one of the caller's own runs into their curated replay collection
   * ({@link import('./dataModel').UserProfile.savedReplayIds}). Quota-bounded by
   * the user's tier ({@link import('./dataModel').savedReplayQuota}); saving over
   * quota rejects server-side. A run must be SAVED before it can be starred. The
   * Function writes `savedReplayIds`. NAMED-ONLY.
   */
  saveReplay(runId: string): Promise<void>;

  /**
   * Remove a run from the caller's SAVED collection. If the run was also STARRED it
   * is un-starred as a side effect (a starred replay must remain saved). The
   * Function rewrites `savedReplayIds` (and `starredReplayIds`/`cards/{uid}` if
   * needed). NAMED-ONLY.
   */
  unsaveReplay(runId: string): Promise<void>;

  /**
   * Load a full {@link RunRecord} for playback (seed + input timeline), or null
   * when the run id is unknown. ANON-ALLOWED (runs are public-readable so POPSTAR
   * LIVE can play them back).
   */
  getReplay(runId: string): Promise<RunRecord | null>;

  /**
   * STAR one of the caller's own runs for their public showcase card. Quota-bounded
   * by the user's tier ({@link import('./dataModel').replayStarQuota}); starring
   * over quota rejects server-side. Updates the public `cards/{uid}`. NAMED-ONLY.
   */
  setStarredReplay(runId: string): Promise<void>;

  /** Remove a run from the caller's starred set (updates `cards/{uid}`). NAMED-ONLY. */
  unstarReplay(runId: string): Promise<void>;

  /**
   * Read the PUBLIC `cards/{uid}` showcase card for any user (their level,
   * equipped sticker, and featured starred replay). ANON-ALLOWED (public read).
   */
  getUserCard(uid: string): Promise<UserCard>;

  // ---------------------------------------------------------------------------
  // Admin (gated by the Firebase Auth custom claim `admin === true`, granted to
  // tech@vfxpop.com at deploy). {@link GameServices.isAdmin} reflects the claim on
  // the current user; {@link GameServices.callAdmin} invokes an `admin.*` action
  // on the single `api` callable and throws if the caller lacks the claim.
  // ---------------------------------------------------------------------------

  /**
   * Whether the current user holds the `admin === true` custom claim. Synchronous
   * (read from the cached ID-token claims); the SERVER re-verifies the claim on
   * every {@link GameServices.callAdmin} regardless, so this is a UI convenience
   * (show/hide the admin panel), never a security boundary. ANON-ALLOWED (returns
   * false for anon/non-admin users).
   */
  isAdmin(): boolean;

  /**
   * Invoke an `admin.*` action on the single `api` callable. `action` is one of the
   * `admin.*` {@link import('../../functions/src/contract').ApiAction}s (e.g.
   * `'setAntiCheat'` / `'admin.setAntiCheat'` — the backend accepts the namespaced
   * form); `payload` is that action's payload. The SERVER enforces the admin claim
   * and throws `permission-denied` for non-admins. ADMIN-ONLY.
   *
   * @param action the admin action name.
   * @param payload the action-specific payload.
   * @returns the action's result (caller narrows to the expected shape).
   */
  callAdmin(action: string, payload?: unknown): Promise<unknown>;
}
